<template>
  <div class="container mx-auto px-4 py-4">
    <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">{{ pageTitle }}</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ pageSubtitle }}
            </p>
          </div>
          <div class="flex space-x-3">
            <Button
              label="Create New Event"
              severity="primary"
              icon="pi pi-plus"
              @click="openEventTypeModal"
            />
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>

        <div v-else-if="events.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No events found. Create your first event to get started!
          </p>
        </div>

        <div v-else-if="filteredEvents.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No {{ getEventTypeLabel(activeTypeFilter).toLowerCase() }} events found.
          </p>
        </div>

        <div v-else>
          <DataTable
            :value="filteredEvents"
            :loading="loading"
            striped-rows
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
            :pt="{
              root: { class: 'border border-gray-200 rounded-lg' }
            }"
          >
            <Column header="" :exportable="false" style="width: 60px">
              <template #body="{ data }">
                <button
                  class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  title="Show QR Code"
                  @click="openQRModal(data)"
                >
                  <i class="pi pi-qrcode text-xl"></i>
                </button>
              </template>
            </Column>

            <Column field="name" header="Name" sortable>
              <template #body="{ data }">
                <div class="text-sm font-medium text-gray-900 dark:text-white">{{ data.name }}</div>
              </template>
            </Column>

            <Column field="location" header="Location" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  {{ data.location || "-" }}
                </div>
              </template>
            </Column>

            <Column field="eventType" header="Format" sortable>
              <template #body="{ data }">
                <span
                  :class="getEventTypeBadgeClass(data.eventType || EVENT_TYPES.CONCERT)"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ getEventTypeLabel(data.eventType || EVENT_TYPES.CONCERT) }}
                </span>
              </template>
            </Column>

            <Column field="isPrivate" header="Type" sortable>
              <template #body="{ data }">
                <span
                  :class="
                    data.isPrivate ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                  "
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ data.isPrivate ? "Private" : "Public" }}
                </span>
              </template>
            </Column>

            <Column field="isDisabled" header="Status" sortable>
              <template #body="{ data }">
                <span
                  :class="getEventStatusClass(data)"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ getEventStatus(data) }}
                </span>
              </template>
            </Column>

            <Column field="votingStartTime" header="Voting Start" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  {{ formatDateTime(data.votingStartTime) }}
                </div>
              </template>
            </Column>

            <Column header="Actions" :exportable="false" style="width: 100px">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <button
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Edit event"
                    @click="openEditModal(data)"
                  >
                    <PencilIcon class="h-5 w-5" />
                  </button>
                  <button
                    :disabled="deletingEventId === data.id"
                    class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :title="deletingEventId === data.id ? 'Deleting...' : 'Delete event'"
                    @click="handleDeleteEvent(data)"
                  >
                    <TrashIcon class="h-5 w-5" />
                  </button>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Event Type Picker Modal -->
    <EventTypePickerModal
      :visible="showEventTypeModal"
      @select="onEventTypeSelected"
      @close="closeEventTypeModal"
    />

    <!-- Add/Edit Event Modal -->
    <div
      v-if="showEventModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
      @click.self="handleOutsideClick"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-3xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto"
      >
        <div class="p-6">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ editingEvent ? "Edit Event" : "Create New Event" }}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{
                editingEvent
                  ? "Update event information"
                  : "Create a new " +
                    getEventTypeLabel(eventForm.eventType).toLowerCase() +
                    " event"
              }}
            </p>
            <span
              v-if="!editingEvent"
              :class="getEventTypeBadgeClass(eventForm.eventType)"
              class="inline-flex mt-2 px-2.5 py-0.5 text-xs font-semibold rounded-full"
            >
              {{ getEventTypeLabel(eventForm.eventType) }}
            </span>
            <span
              v-else
              :class="getEventTypeBadgeClass(eventForm.eventType)"
              class="inline-flex mt-2 px-2.5 py-0.5 text-xs font-semibold rounded-full"
            >
              {{ getEventTypeLabel(eventForm.eventType) }}
            </span>
          </div>

          <form class="space-y-6" @submit.prevent="handleSaveEvent">
            <!-- Always visible: Name & Slug -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div class="md:col-span-2">
                <label
                  for="event-name"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >Name <span class="text-red-500">*</span></label
                >
                <input
                  id="event-name"
                  v-model="eventForm.name"
                  type="text"
                  required
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter event name"
                />
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Name is shown on the header area of the voting page.
                </p>
              </div>

              <div class="md:col-span-2">
                <label
                  for="event-slug"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >Event Slug</label
                >
                <input
                  id="event-slug"
                  v-model="eventForm.slug"
                  type="text"
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                  placeholder="event-slug-example"
                  pattern="[a-z0-9\-]+"
                  title="Only lowercase letters, numbers, and hyphens are allowed"
                />
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Used in the event URL. Only lowercase letters, numbers, and hyphens allowed.
                </p>
              </div>
            </div>

            <!-- Accordion sections -->
            <Accordion :value="openAccordionPanels" class="event-accordion">
              <!-- Schedule -->
              <AccordionPanel value="schedule">
                <AccordionHeader>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-calendar" style="font-size: 14px"></i>
                    Schedule
                  </span>
                </AccordionHeader>
                <AccordionContent>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                    <div v-if="isSetlistAllowed(eventForm.eventType)">
                      <label
                        for="event-start-time"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >Event Start Time <span class="text-red-500">*</span></label
                      >
                      <DatePicker
                        id="event-start-time"
                        v-model="eventForm.eventStartTime"
                        show-time
                        hour-format="24"
                        :show-icon="true"
                        date-format="yy-mm-dd"
                        :time-only="false"
                        :step-minute="1"
                        class="w-full"
                        input-class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        :required="true"
                      />
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        When the live event begins. Does not affect voting availability.
                      </p>
                    </div>

                    <div v-if="isSetlistAllowed(eventForm.eventType)">
                      <label
                        for="event-end-time"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >Event End Time <span class="text-red-500">*</span></label
                      >
                      <DatePicker
                        id="event-end-time"
                        v-model="eventForm.eventEndTime"
                        show-time
                        hour-format="24"
                        :show-icon="true"
                        date-format="yy-mm-dd"
                        :time-only="false"
                        :step-minute="1"
                        class="w-full"
                        input-class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        :required="true"
                      />
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        When the event ends. After this time, the event page shows as ended and
                        voting is no longer possible.
                      </p>
                    </div>

                    <div>
                      <label
                        for="event-voting-start"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        <template v-if="isKaraoke(eventForm.eventType)">Event Start Time</template>
                        <template v-else>Voting Start Time</template>
                        <span class="text-red-500">*</span>
                      </label>
                      <DatePicker
                        id="event-voting-start"
                        v-model="eventForm.votingStartTime"
                        show-time
                        hour-format="24"
                        :show-icon="true"
                        date-format="yy-mm-dd"
                        :time-only="false"
                        :step-minute="1"
                        class="w-full"
                        input-class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        :required="true"
                      />
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <template v-if="isKaraoke(eventForm.eventType)">
                          When the karaoke session begins.
                        </template>
                        <template v-else>
                          Can be set before event start to allow early voting.
                        </template>
                      </p>
                    </div>

                    <div>
                      <label
                        for="event-voting-end"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        <template v-if="isKaraoke(eventForm.eventType)">Event End Time</template>
                        <template v-else>Voting End Time</template>
                        <span v-if="isPrevoting(eventForm.eventType)" class="text-red-500">*</span>
                      </label>
                      <DatePicker
                        id="event-voting-end"
                        v-model="eventForm.votingEndTime"
                        show-time
                        hour-format="24"
                        :show-icon="true"
                        date-format="yy-mm-dd"
                        :time-only="false"
                        :step-minute="1"
                        class="w-full"
                        input-class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        :required="isPrevoting(eventForm.eventType)"
                      />
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <template v-if="isKaraoke(eventForm.eventType)">
                          When the karaoke session ends.
                        </template>
                        <template v-else-if="isPrevoting(eventForm.eventType)">
                          Required. Defines when pre-voting closes.
                        </template>
                        <template v-else>
                          Optional. If not set, voting stays open until manually disabled or event
                          ends.
                        </template>
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>

              <!-- Details -->
              <AccordionPanel value="details">
                <AccordionHeader>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-info-circle" style="font-size: 14px"></i>
                    Details
                  </span>
                </AccordionHeader>
                <AccordionContent>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                    <div class="md:col-span-2">
                      <label
                        for="event-theme"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Theme</label
                      >
                      <Select
                        id="event-theme"
                        v-model="eventForm.theme"
                        :options="themeOptions"
                        option-label="label"
                        option-value="value"
                        placeholder="Select theme"
                        class="mt-1 w-full"
                        :show-clear="false"
                      />
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Define the 'default' theme of the voting page when users open it.
                      </p>
                    </div>

                    <div class="md:col-span-2">
                      <label
                        for="event-concerts-style"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Concerts page style</label
                      >
                      <Select
                        id="event-concerts-style"
                        v-model="eventForm.concertsStyle"
                        :options="concertsStyleOptions"
                        option-label="label"
                        option-value="value"
                        placeholder="Select style"
                        class="mt-1 w-full"
                        :show-clear="false"
                      />
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Visual style for the public concerts page, including screen and LED views.
                      </p>
                    </div>

                    <div>
                      <label
                        for="event-location"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Location</label
                      >
                      <input
                        id="event-location"
                        v-model="eventForm.location"
                        type="text"
                        class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter location"
                      />
                    </div>

                    <div>
                      <label
                        for="event-organizer"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Organizer Name</label
                      >
                      <input
                        id="event-organizer"
                        v-model="eventForm.organizerName"
                        type="text"
                        class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter organizer name"
                      />
                    </div>

                    <div class="md:col-span-2">
                      <label
                        for="event-notes"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Notes</label
                      >
                      <textarea
                        id="event-notes"
                        v-model="eventForm.notes"
                        rows="3"
                        class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter optional notes"
                      ></textarea>
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Internal notes for admin use only. Not visible to the audience.
                      </p>
                    </div>

                    <div class="md:col-span-2">
                      <div class="flex items-center gap-3">
                        <Checkbox
                          v-model="eventForm.isPrivate"
                          input-id="event-private"
                          binary
                          size="large"
                        />
                        <label
                          for="event-private"
                          class="block text-sm text-gray-900 dark:text-gray-200 cursor-pointer"
                        >
                          Private Event
                        </label>
                      </div>
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Selection for own use only to differentiate between public and private
                        events. Not visible to the audience.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>

              <!-- Language -->
              <AccordionPanel value="language">
                <AccordionHeader>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-language" style="font-size: 14px"></i>
                    Language
                  </span>
                </AccordionHeader>
                <AccordionContent>
                  <div class="grid grid-cols-1 gap-6 pt-2">
                    <div>
                      <label
                        for="event-enabled-locales"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Languages on event page</label
                      >
                      <MultiSelect
                        id="event-enabled-locales"
                        v-model="eventForm.enabledLocales"
                        :options="localeOptions"
                        option-label="label"
                        option-value="value"
                        placeholder="Select languages for the language switcher"
                        display="chip"
                        class="mt-1 w-full"
                      />
                      <p
                        v-if="eventForm.enabledLocales.length === 0"
                        class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                      >
                        No selection: English will be used and the language switcher will be hidden.
                      </p>
                      <p v-else class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Customers can switch between the selected languages on the event page.
                      </p>
                    </div>

                    <div v-if="eventForm.enabledLocales.length > 0">
                      <label
                        for="event-default-locale"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Default language</label
                      >
                      <Select
                        id="event-default-locale"
                        v-model="eventForm.defaultLocale"
                        :options="defaultLocaleOptions"
                        option-label="label"
                        option-value="value"
                        placeholder="Select default language"
                        class="mt-1 w-full"
                        :show-clear="false"
                      />
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Language shown when customers open the event page via QR code or direct link.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>

              <!-- Song Management -->
              <AccordionPanel value="songs">
                <AccordionHeader>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-play-circle" style="font-size: 14px"></i>
                    Song Management
                  </span>
                </AccordionHeader>
                <AccordionContent>
                  <div class="space-y-8 pt-2">
                    <div
                      v-if="!isPrevoting(eventForm.eventType) && !isKaraoke(eventForm.eventType)"
                      class="flex items-start justify-between gap-4"
                    >
                      <div>
                        <label
                          for="event-auto-queue"
                          class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Auto Queue Next Song
                        </label>
                        <p class="text-xs text-gray-500 mt-1">
                          Automatically locks the next highest-voted song as "Up Next" when a song
                          starts performing
                        </p>
                      </div>
                      <ToggleSwitch
                        id="event-auto-queue"
                        v-model="eventForm.autoQueueNext"
                        :class="[
                          'shrink-0',
                          eventForm.autoQueueNext ? 'event-auto-queue-toggle' : ''
                        ]"
                      />
                    </div>

                    <div>
                      <label
                        for="event-discarded-categories"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Exclude categories
                      </label>
                      <MultiSelect
                        id="event-discarded-categories"
                        v-model="eventForm.discardedCategories"
                        :options="categories"
                        option-label="name"
                        option-value="name"
                        placeholder="Select categories to exclude from this event"
                        display="chip"
                        :filter="true"
                        class="mt-1 w-full"
                        :loading="categoriesLoading"
                      />
                      <p class="mt-1 text-xs text-gray-500">
                        All songs in selected categories will be hidden. If a song has multiple
                        categories, it is hidden when any one of them is excluded.
                      </p>
                    </div>

                    <div>
                      <label
                        for="event-discarded-songs"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Exclude individual songs
                      </label>
                      <MultiSelect
                        id="event-discarded-songs"
                        v-model="eventForm.discardedSongs"
                        :options="songsForIndividualExclusion"
                        option-label="title"
                        option-value="id"
                        placeholder="Select additional songs to remove from this event's voting list"
                        display="chip"
                        :filter="true"
                        class="mt-1 w-full"
                        :loading="songsLoading"
                      >
                        <template #option="{ option }">
                          <div class="flex flex-col">
                            <span class="font-medium">{{ option.title }}</span>
                            <span class="text-xs text-gray-500">{{ option.author }}</span>
                          </div>
                        </template>
                      </MultiSelect>
                      <p class="mt-1 text-xs text-gray-500">
                        For one-off exclusions, including uncategorized songs. Songs already
                        excluded by category are not shown here.
                      </p>
                      <p
                        v-if="exclusionSummaryText"
                        class="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400"
                      >
                        {{ exclusionSummaryText }}
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>

              <!-- Setlist -->
              <AccordionPanel v-if="isSetlistAllowed(eventForm.eventType)" value="setlist">
                <AccordionHeader>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-list-check" style="font-size: 14px"></i>
                    Setlist
                  </span>
                </AccordionHeader>
                <AccordionContent>
                  <div class="space-y-8 pt-2">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <label
                          for="event-setlist-enabled"
                          class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Enable predefined setlist length
                        </label>
                        <p class="text-xs text-gray-500 mt-1">
                          Shows a "set end" marker in the voting list so the audience can see which
                          songs will fit into the current set.
                        </p>
                      </div>
                      <ToggleSwitch
                        id="event-setlist-enabled"
                        v-model="eventForm.setlistEnabled"
                        :class="[
                          'shrink-0',
                          eventForm.setlistEnabled ? 'event-setlist-toggle' : ''
                        ]"
                      />
                    </div>

                    <div v-if="eventForm.setlistEnabled" class="space-y-3">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Set durations (minutes)</label
                      >
                      <div
                        v-for="(duration, index) in eventForm.setlistDurations"
                        :key="index"
                        class="flex items-center gap-3"
                      >
                        <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-14"
                          >Set {{ index + 1 }}</span
                        >
                        <input
                          type="number"
                          :value="duration"
                          min="1"
                          max="300"
                          class="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="min"
                          @input="updateSetDuration(index, $event.target.value)"
                        />
                        <span class="text-sm text-gray-500 dark:text-gray-400">min</span>
                        <button
                          type="button"
                          class="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title="Remove this set"
                          :disabled="eventForm.setlistDurations.length <= 1"
                          :class="{
                            'opacity-30 cursor-not-allowed': eventForm.setlistDurations.length <= 1
                          }"
                          @click="removeSetDuration(index)"
                        >
                          <i class="pi pi-trash" style="font-size: 14px"></i>
                        </button>
                      </div>
                      <button
                        type="button"
                        class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-dashed border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        @click="addSetDuration"
                      >
                        <i class="pi pi-plus" style="font-size: 12px"></i>
                        Add set
                      </button>
                      <p class="text-xs text-gray-400 dark:text-gray-500">
                        Total:
                        {{
                          eventForm.setlistDurations.reduce((sum, d) => sum + (parseInt(d) || 0), 0)
                        }}
                        minutes across {{ eventForm.setlistDurations.length }} set{{
                          eventForm.setlistDurations.length !== 1 ? "s" : ""
                        }}
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>

              <!-- Controls -->
              <AccordionPanel value="controls">
                <AccordionHeader>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-cog" style="font-size: 14px"></i>
                    Controls
                  </span>
                </AccordionHeader>
                <AccordionContent>
                  <div class="space-y-8 pt-2">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <label
                          for="event-disabled"
                          class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Disable Event
                        </label>
                        <p class="text-xs text-gray-500 mt-1">
                          Closes the event and takes the landing page offline
                        </p>
                      </div>
                      <ToggleSwitch
                        id="event-disabled"
                        v-model="eventForm.isDisabled"
                        :class="['shrink-0', eventForm.isDisabled ? 'event-disabled-toggle' : '']"
                      />
                    </div>
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <label
                          for="event-voting-disabled"
                          class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Disable Voting
                        </label>
                        <p class="text-xs text-gray-500 mt-1">
                          Prevents voting but keeps the event page accessible
                        </p>
                      </div>
                      <ToggleSwitch
                        id="event-voting-disabled"
                        v-model="eventForm.isVotingDisabled"
                        :class="[
                          'shrink-0',
                          eventForm.isVotingDisabled ? 'event-voting-disabled-toggle' : ''
                        ]"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionPanel>
            </Accordion>

            <div
              v-if="eventError"
              class="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3"
            >
              {{ eventError }}
            </div>

            <div class="flex justify-end space-x-3 pt-4">
              <Button type="button" label="Cancel" severity="secondary" @click="cancelEventModal" />
              <Button
                type="submit"
                :disabled="eventLoading"
                :label="eventLoading ? 'Saving...' : 'Save'"
                severity="primary"
                :loading="eventLoading"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- QR Code Modal -->
    <div
      v-if="showQRModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeQRModal"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="mb-4">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Event QR Code</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ selectedEventForQR?.name || "" }}
            </p>
          </div>

          <div class="flex flex-col items-center justify-center py-6">
            <div v-if="qrCodeDataUrl" class="mb-4">
              <img
                :src="qrCodeDataUrl"
                alt="QR Code"
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900"
              />
            </div>
            <div v-else class="mb-4 flex items-center justify-center" style="min-height: 200px">
              <i class="pi pi-spin pi-spinner text-3xl text-gray-500 dark:text-gray-400"></i>
            </div>

            <div class="mt-4 text-center w-full">
              <!-- Event Code Display -->
              <div
                v-if="selectedEventForQR?.eventCode"
                class="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <p class="text-xs text-gray-600 dark:text-gray-300 mb-1">Event Code:</p>
                <p
                  class="text-2xl font-mono font-bold text-gray-900 dark:text-white tracking-wider"
                >
                  {{ selectedEventForQR.eventCode }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Share this code for quick access
                </p>
              </div>
              <div
                v-else
                class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800"
              >
                <p class="text-xs text-yellow-800 dark:text-yellow-200 mb-2">
                  No event code generated
                </p>
                <Button
                  label="Generate Code"
                  severity="warning"
                  size="small"
                  :loading="generatingCode"
                  icon="pi pi-key"
                  @click="generateEventCode"
                />
              </div>

              <!-- Event URL Display -->
              <div class="mt-4">
                <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">Event URL:</p>
                <p
                  v-if="selectedEventForQR?.slug"
                  class="text-sm font-mono font-medium text-gray-900 dark:text-white break-all px-2"
                >
                  {{ getEventUrl(selectedEventForQR) }}
                </p>
                <p v-else class="text-sm text-orange-600 dark:text-orange-400 px-2">
                  This event does not have a slug. Please add a slug to generate a QR code.
                </p>
                <button
                  v-if="selectedEventForQR?.slug"
                  class="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200"
                  :class="
                    urlCopied
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  "
                  @click="copyEventUrl"
                >
                  <i :class="urlCopied ? 'pi pi-check' : 'pi pi-copy'" class="text-xs"></i>
                  {{ urlCopied ? "Copied!" : "Copy URL" }}
                </button>
              </div>
            </div>
          </div>

          <div
            class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700"
          >
            <button type="button" class="btn btn-secondary" @click="closeQRModal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20"
            >
              <svg
                class="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
            Delete Event
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Are you sure you want to delete
            <strong class="dark:text-white">{{ eventToDelete?.name }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <button class="btn btn-secondary flex-1" @click="cancelDelete">Cancel</button>
            <button
              :disabled="deletingEventId !== null"
              class="btn btn-danger flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="confirmDelete"
            >
              {{ deletingEventId !== null ? "Deleting..." : "Delete" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { formatDateTime, formatDate } from "@/utils/dateFormat";
import { isSongExcludedForEvent, countExcludedSongs } from "@/utils/eventSongFilter";
import {
  EVENT_TYPES,
  getEventTypeLabel,
  getEventTypeBadgeClass,
  isPrevoting,
  isKaraoke,
  isSetlistAllowed
} from "@/constants/eventTypes";
import EventTypePickerModal from "@/components/admin/EventTypePickerModal.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import ToggleSwitch from "primevue/toggleswitch";
import Button from "primevue/button";
import Select from "primevue/select";
import MultiSelect from "primevue/multiselect";
import DatePicker from "primevue/datepicker";
import Accordion from "primevue/accordion";
import AccordionPanel from "primevue/accordionpanel";
import AccordionHeader from "primevue/accordionheader";
import AccordionContent from "primevue/accordioncontent";
import { PencilIcon, TrashIcon } from "@heroicons/vue/24/outline";
import QRCode from "qrcode";
import api from "../../services/api";
import { CONCERTS_STYLE_OPTIONS, CONCERTS_STYLES } from "@/constants/concertsStyles";
import { LOCALE_OPTIONS, DEFAULT_LOCALE } from "@/constants/locales";

export default {
  name: "AdminEvents",
  components: {
    DataTable,
    Column,
    Checkbox,
    ToggleSwitch,
    Button,
    Select,
    MultiSelect,
    DatePicker,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    PencilIcon,
    TrashIcon,
    EventTypePickerModal
  },
  setup() {
    const toast = useToast();
    const confirm = useConfirm();
    const route = useRoute();
    const events = ref([]);
    const loading = ref(true);
    const error = ref("");
    const showEventModal = ref(false);
    const showEventTypeModal = ref(false);
    const editingEvent = ref(null);
    const eventForm = ref({
      name: "",
      slug: "",
      eventType: EVENT_TYPES.CONCERT,
      date: "",
      time: "",
      isPrivate: false,
      organizerName: "",
      location: "",
      isDisabled: false,
      isVotingDisabled: false,
      votingStartTime: "",
      votingEndTime: "",
      discardedSongs: [],
      discardedCategories: [],
      pinnedSongs: [],
      notes: "",
      theme: "light",
      concertsStyle: CONCERTS_STYLES.DEFAULT,
      defaultLocale: DEFAULT_LOCALE,
      enabledLocales: [],
      autoQueueNext: false,
      setlistEnabled: false,
      setlistDurations: [45]
    });

    const themeOptions = [
      { label: "Light", value: "light" },
      { label: "Dark", value: "dark" }
    ];
    const concertsStyleOptions = CONCERTS_STYLE_OPTIONS;
    const localeOptions = LOCALE_OPTIONS;

    const defaultLocaleOptions = computed(() =>
      localeOptions.filter((option) => eventForm.value.enabledLocales.includes(option.value))
    );

    watch(
      () => eventForm.value.enabledLocales,
      (enabledLocales) => {
        if (enabledLocales.length === 0) {
          return;
        }

        if (!enabledLocales.includes(eventForm.value.defaultLocale)) {
          eventForm.value.defaultLocale = enabledLocales[0];
        }
      },
      { deep: true }
    );

    const eventError = ref("");
    const eventLoading = ref(false);
    const showDeleteModal = ref(false);
    const eventToDelete = ref(null);
    const deletingEventId = ref(null);
    const rows = ref(10);
    const showQRModal = ref(false);
    const selectedEventForQR = ref(null);
    const qrCodeDataUrl = ref("");
    const generatingCode = ref(false);
    const songs = ref([]);
    const songsLoading = ref(false);
    const categories = ref([]);
    const categoriesLoading = ref(false);

    const songsForIndividualExclusion = computed(() => {
      const previewEvent = {
        discardedCategories: eventForm.value.discardedCategories || [],
        discardedSongs: []
      };
      return songs.value.filter((song) => !isSongExcludedForEvent(song, previewEvent));
    });

    const exclusionSummaryText = computed(() => {
      const stats = countExcludedSongs(songs.value, {
        discardedCategories: eventForm.value.discardedCategories || [],
        discardedSongs: eventForm.value.discardedSongs || []
      });
      if (stats.total === 0) return null;

      const parts = [`${stats.total} song${stats.total !== 1 ? "s" : ""} excluded`];
      const catCount = (eventForm.value.discardedCategories || []).length;
      if (catCount > 0) {
        parts.push(`${catCount} categor${catCount !== 1 ? "ies" : "y"}`);
      }
      if (stats.individualOnly > 0) {
        parts.push(`${stats.individualOnly} individual`);
      }
      return parts.join(" · ");
    });

    // All accordion panels closed by default; only one can be open at a time
    const openAccordionPanels = ref(null);

    const activeTypeFilter = computed(() => {
      const type = route.query.type;
      if (typeof type === "string" && Object.values(EVENT_TYPES).includes(type)) {
        return type;
      }
      return null;
    });

    const filteredEvents = computed(() => {
      if (!activeTypeFilter.value) return events.value;
      return events.value.filter(
        (event) => (event.eventType || EVENT_TYPES.CONCERT) === activeTypeFilter.value
      );
    });

    const pageTitle = computed(() => {
      if (activeTypeFilter.value) {
        return getEventTypeLabel(activeTypeFilter.value);
      }
      return "Events";
    });

    const pageSubtitle = computed(() => {
      if (activeTypeFilter.value) {
        return `${filteredEvents.value.length} of ${events.value.length} events`;
      }
      return `Total: ${events.value.length} events`;
    });

    const fetchCategories = async () => {
      if (categoriesLoading.value || categories.value.length > 0) return;
      try {
        categoriesLoading.value = true;
        const response = await api.get("/api/categories");
        if (response.data.success) {
          categories.value = response.data.data.categories || [];
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      } finally {
        categoriesLoading.value = false;
      }
    };

    const fetchSongs = async () => {
      if (songsLoading.value || songs.value.length > 0) return;
      try {
        songsLoading.value = true;
        const response = await api.get("/api/songs");
        if (response.data.success) {
          songs.value = response.data.data.songs || [];
        }
      } catch (err) {
        console.error("Failed to fetch songs:", err);
      } finally {
        songsLoading.value = false;
      }
    };

    const fetchEvents = async () => {
      try {
        loading.value = true;
        error.value = "";
        const response = await api.get("/api/events");
        if (response.data.success) {
          events.value = response.data.data.events || [];
        } else {
          error.value = response.data.message || "Failed to fetch events";
        }
      } catch (err) {
        console.error("Failed to fetch events:", err);
        error.value = err.response?.data?.message || "Failed to fetch events";
      } finally {
        loading.value = false;
      }
    };

    const openEventTypeModal = () => {
      showEventTypeModal.value = true;
    };

    const closeEventTypeModal = () => {
      showEventTypeModal.value = false;
    };

    const onEventTypeSelected = (eventType) => {
      closeEventTypeModal();
      openAddModal(eventType);
    };

    const openAddModal = (eventType = EVENT_TYPES.CONCERT) => {
      editingEvent.value = null;
      const isConcert = eventType === EVENT_TYPES.CONCERT;
      eventForm.value = {
        name: "",
        slug: "",
        eventType,
        eventStartTime: null,
        eventEndTime: null,
        isPrivate: false,
        organizerName: "",
        location: "",
        isDisabled: false,
        isVotingDisabled: false,
        votingStartTime: null,
        votingEndTime: null,
        discardedSongs: [],
        discardedCategories: [],
        pinnedSongs: [],
        notes: "",
        theme: "light",
        concertsStyle: CONCERTS_STYLES.DEFAULT,
        defaultLocale: DEFAULT_LOCALE,
        enabledLocales: [],
        autoQueueNext: false,
        setlistEnabled: false,
        setlistDurations: isConcert ? [45] : []
      };
      eventError.value = "";
      showEventModal.value = true;
      fetchSongs();
      fetchCategories();
    };

    const openEditModal = (event) => {
      editingEvent.value = event;
      // Convert date strings to Date objects for PrimeVue DatePicker component
      const parseDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString);
      };

      eventForm.value = {
        name: event.name || "",
        slug: event.slug || "",
        eventType: event.eventType || EVENT_TYPES.CONCERT,
        eventStartTime: parseDate(event.eventStartTime),
        eventEndTime: parseDate(event.eventEndTime),
        isPrivate: event.isPrivate || false,
        organizerName: event.organizerName || "",
        location: event.location || "",
        isDisabled: event.isDisabled || false,
        votingStartTime: parseDate(event.votingStartTime),
        votingEndTime: parseDate(event.votingEndTime),
        discardedSongs: event.discardedSongs || [],
        discardedCategories: event.discardedCategories || [],
        pinnedSongs: event.pinnedSongs || [],
        notes: event.notes || "",
        theme: event.theme || "light",
        concertsStyle: event.concertsStyle || CONCERTS_STYLES.DEFAULT,
        defaultLocale: event.defaultLocale || DEFAULT_LOCALE,
        enabledLocales: event.enabledLocales || [],
        autoQueueNext: event.autoQueueNext || false,
        setlistEnabled: event.setlistEnabled || false,
        setlistDurations:
          event.setlistDurations && event.setlistDurations.length > 0
            ? [...event.setlistDurations]
            : [45]
      };
      eventError.value = "";
      showEventModal.value = true;
      fetchSongs();
      fetchCategories();
    };

    const cancelEventModal = () => {
      showEventModal.value = false;
      editingEvent.value = null;
      eventForm.value = {
        name: "",
        slug: "",
        eventType: EVENT_TYPES.CONCERT,
        eventStartTime: null,
        eventEndTime: null,
        isPrivate: false,
        organizerName: "",
        location: "",
        isDisabled: false,
        isVotingDisabled: false,
        votingStartTime: null,
        votingEndTime: null,
        discardedSongs: [],
        discardedCategories: [],
        pinnedSongs: [],
        notes: "",
        theme: "light",
        concertsStyle: CONCERTS_STYLES.DEFAULT,
        defaultLocale: DEFAULT_LOCALE,
        enabledLocales: [],
        autoQueueNext: false,
        setlistEnabled: false,
        setlistDurations: [45]
      };
      eventError.value = "";
    };

    // Setlist duration helpers
    const addSetDuration = () => {
      eventForm.value.setlistDurations.push(45);
    };

    const removeSetDuration = (index) => {
      if (eventForm.value.setlistDurations.length > 1) {
        eventForm.value.setlistDurations.splice(index, 1);
      }
    };

    const updateSetDuration = (index, value) => {
      const parsed = parseInt(value) || 0;
      eventForm.value.setlistDurations[index] = parsed;
    };

    const handleOutsideClick = () => {
      // Only show confirmation when adding a new event (not editing)
      if (!editingEvent.value) {
        // Check if form has any data entered
        const hasFormData =
          eventForm.value.name.trim() ||
          eventForm.value.slug.trim() ||
          eventForm.value.eventStartTime ||
          eventForm.value.eventEndTime ||
          eventForm.value.organizerName.trim() ||
          eventForm.value.location.trim() ||
          eventForm.value.votingStartTime ||
          eventForm.value.votingEndTime ||
          eventForm.value.notes.trim() ||
          eventForm.value.isPrivate ||
          eventForm.value.isDisabled ||
          eventForm.value.theme;

        if (hasFormData) {
          confirm.require({
            message:
              "Are you sure you want to cancel creating this event? Any unsaved changes will be lost.",
            header: "Cancel Creating Event",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
              label: "No, Continue Editing",
              severity: "secondary",
              outlined: true
            },
            acceptProps: {
              label: "Yes, Cancel",
              severity: "danger"
            },
            accept: () => {
              cancelEventModal();
            },
            reject: () => {
              // User chose to continue editing, do nothing
            }
          });
        } else {
          // No data entered, close without confirmation
          cancelEventModal();
        }
      } else {
        // Editing mode - close without confirmation (existing behavior)
        cancelEventModal();
      }
    };

    const handleSaveEvent = async () => {
      try {
        eventLoading.value = true;
        eventError.value = "";

        const url = editingEvent.value ? `/api/events/${editingEvent.value.id}` : "/api/events";

        const method = editingEvent.value ? "put" : "post";

        // Convert Date objects to ISO strings for API
        const formatDateForAPI = (date) => {
          if (!date) return null;
          if (date instanceof Date) {
            return date.toISOString();
          }
          return date;
        };

        if (isPrevoting(eventForm.value.eventType) && !eventForm.value.votingEndTime) {
          eventError.value = "Voting end time is required for pre-voting events.";
          return;
        }

        const payload = {
          name: eventForm.value.name.trim(),
          slug: eventForm.value.slug.trim() || null,
          eventType: eventForm.value.eventType,
          eventStartTime: formatDateForAPI(eventForm.value.eventStartTime),
          eventEndTime: formatDateForAPI(eventForm.value.eventEndTime),
          isPrivate: eventForm.value.isPrivate,
          organizerName: eventForm.value.organizerName.trim() || null,
          location: eventForm.value.location.trim() || null,
          isDisabled: eventForm.value.isDisabled,
          isVotingDisabled: eventForm.value.isVotingDisabled,
          votingStartTime: formatDateForAPI(eventForm.value.votingStartTime),
          votingEndTime: formatDateForAPI(eventForm.value.votingEndTime),
          discardedSongs: eventForm.value.discardedSongs,
          discardedCategories: eventForm.value.discardedCategories,
          pinnedSongs: eventForm.value.pinnedSongs,
          notes: eventForm.value.notes.trim() || null,
          theme: eventForm.value.theme || "light",
          concertsStyle: eventForm.value.concertsStyle || CONCERTS_STYLES.DEFAULT,
          defaultLocale: eventForm.value.defaultLocale || DEFAULT_LOCALE,
          enabledLocales: eventForm.value.enabledLocales || [],
          autoQueueNext: eventForm.value.autoQueueNext || false,
          setlistEnabled: eventForm.value.setlistEnabled || false,
          setlistDurations: eventForm.value.setlistEnabled
            ? eventForm.value.setlistDurations.map((d) => parseInt(d) || 0).filter((d) => d > 0)
            : []
        };

        if (isPrevoting(eventForm.value.eventType) || isKaraoke(eventForm.value.eventType)) {
          // Both prevoting and karaoke derive event start/end from the session (voting) times
          // since the Event Start/End pickers are hidden for these types.
          payload.eventStartTime = formatDateForAPI(eventForm.value.votingStartTime);
          payload.eventEndTime = formatDateForAPI(eventForm.value.votingEndTime);
          payload.setlistEnabled = false;
          payload.setlistDurations = [];
          payload.autoQueueNext = false;
        }

        const response = await api[method](url, payload);

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: editingEvent.value
              ? "Event updated successfully"
              : "Event created successfully",
            life: 3000
          });

          await fetchEvents();
          cancelEventModal();
        } else {
          eventError.value = response.data.message || "Failed to save event";
        }
      } catch (err) {
        console.error("Failed to save event:", err);
        eventError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to save event";
      } finally {
        eventLoading.value = false;
      }
    };

    const handleDeleteEvent = (event) => {
      eventToDelete.value = event;
      showDeleteModal.value = true;
    };

    const cancelDelete = () => {
      showDeleteModal.value = false;
      eventToDelete.value = null;
    };

    const confirmDelete = async () => {
      if (!eventToDelete.value) return;

      try {
        deletingEventId.value = eventToDelete.value.id;
        error.value = "";

        const response = await api.delete(`/api/events/${deletingEventId.value}`);

        if (response.data.success) {
          events.value = events.value.filter((e) => e.id !== deletingEventId.value);
          showDeleteModal.value = false;
          eventToDelete.value = null;
        } else {
          error.value = response.data.message || "Failed to delete event";
        }
      } catch (err) {
        console.error("Failed to delete event:", err);
        error.value = err.response?.data?.message || "Failed to delete event";
      } finally {
        deletingEventId.value = null;
      }
    };

    const getEventStatus = (event) => {
      const now = new Date();
      const startTime = new Date(event.votingStartTime);
      const endTime = event.votingEndTime ? new Date(event.votingEndTime) : null;

      if (event.isDisabled) return "Disabled";
      if (now < startTime) return "Upcoming";
      if (endTime && now > endTime) return "Ended";
      return "Active";
    };

    const openQRModal = async (event) => {
      selectedEventForQR.value = event;
      showQRModal.value = true;
      qrCodeDataUrl.value = "";

      try {
        // Generate QR code with event URL using concerts service URL + event slug
        // The concerts service is a separate service handling voting
        const concertsUrl = import.meta.env.VITE_CONCERTS_URL || window.location.origin;

        // QR code must use the event slug
        if (!event.slug) {
          console.warn("Event does not have a slug. Please set a slug to generate QR code.");
          qrCodeDataUrl.value = "";
          return;
        }

        const eventUrl = `${concertsUrl}/${event.slug}?lang=${event.defaultLocale || DEFAULT_LOCALE}`;

        const dataUrl = await QRCode.toDataURL(eventUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: "#000000",
            light: "#FFFFFF"
          }
        });
        qrCodeDataUrl.value = dataUrl;
      } catch (error) {
        console.error("Failed to generate QR code:", error);
      }
    };

    const generateEventCode = async () => {
      if (!selectedEventForQR.value) return;

      generatingCode.value = true;
      try {
        // Update the event to trigger code generation (backend generates code if missing)
        const response = await api.put(`/api/events/${selectedEventForQR.value.id}`, {
          name: selectedEventForQR.value.name,
          slug: selectedEventForQR.value.slug,
          date: selectedEventForQR.value.date,
          // Include other fields to ensure update works
          isPrivate: selectedEventForQR.value.isPrivate,
          location: selectedEventForQR.value.location,
          votingStartTime: selectedEventForQR.value.votingStartTime,
          votingEndTime: selectedEventForQR.value.votingEndTime
        });

        if (response.data.success) {
          // Update the selected event with the new code
          selectedEventForQR.value.eventCode = response.data.data.event.eventCode;
          // Refresh events list to get updated code
          await fetchEvents();
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Event code generated successfully",
            life: 3000
          });
        }
      } catch (error) {
        console.error("Failed to generate event code:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to generate event code",
          life: 5000
        });
      } finally {
        generatingCode.value = false;
      }
    };

    const closeQRModal = () => {
      showQRModal.value = false;
      selectedEventForQR.value = null;
      qrCodeDataUrl.value = "";
    };

    const getEventUrl = (event) => {
      if (!event) return "";
      if (!event.slug) return "Event slug required for URL";
      const concertsUrl = import.meta.env.VITE_CONCERTS_URL || window.location.origin;
      return `${concertsUrl}/${event.slug}?lang=${event.defaultLocale || DEFAULT_LOCALE}`;
    };

    const urlCopied = ref(false);
    let copyTimeout = null;

    const copyEventUrl = async () => {
      const url = getEventUrl(selectedEventForQR.value);
      if (!url || url.includes("required")) return;
      try {
        await navigator.clipboard.writeText(url);
        urlCopied.value = true;
        clearTimeout(copyTimeout);
        copyTimeout = setTimeout(() => {
          urlCopied.value = false;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy URL:", err);
      }
    };

    const getEventStatusClass = (event) => {
      const status = getEventStatus(event);
      switch (status) {
        case "Active":
          return "bg-green-100 text-green-800";
        case "Upcoming":
          return "bg-blue-100 text-blue-800";
        case "Ended":
          return "bg-gray-100 text-gray-800";
        case "Disabled":
          return "bg-red-100 text-red-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    onMounted(() => {
      fetchEvents();
    });

    return {
      events,
      filteredEvents,
      activeTypeFilter,
      pageTitle,
      pageSubtitle,
      loading,
      error,
      fetchEvents,
      showEventModal,
      showEventTypeModal,
      editingEvent,
      eventForm,
      EVENT_TYPES,
      getEventTypeLabel,
      getEventTypeBadgeClass,
      isPrevoting,
      isKaraoke,
      isSetlistAllowed,
      themeOptions,
      concertsStyleOptions,
      localeOptions,
      defaultLocaleOptions,
      eventError,
      eventLoading,
      openEventTypeModal,
      closeEventTypeModal,
      onEventTypeSelected,
      openAddModal,
      openEditModal,
      cancelEventModal,
      handleOutsideClick,
      handleSaveEvent,
      handleDeleteEvent,
      showDeleteModal,
      eventToDelete,
      deletingEventId,
      cancelDelete,
      confirmDelete,
      showQRModal,
      selectedEventForQR,
      qrCodeDataUrl,
      generatingCode,
      openQRModal,
      closeQRModal,
      generateEventCode,
      getEventUrl,
      urlCopied,
      copyEventUrl,
      songs,
      songsLoading,
      categories,
      categoriesLoading,
      songsForIndividualExclusion,
      exclusionSummaryText,
      openAccordionPanels,
      addSetDuration,
      removeSetDuration,
      updateSetDuration,
      formatDate,
      formatDateTime,
      getEventStatus,
      getEventStatusClass,
      rows
    };
  }
};
</script>

<style scoped>
/* Add top border to first accordion panel to match the borders between panels */
:deep(.event-accordion .p-accordionpanel:first-child) {
  border-top: 1px solid;
}

/* Style ToggleSwitch ON state (when event is disabled) to be red */
:deep(.event-disabled-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #ef4444 !important; /* red-500 */
}

/* Keep the toggle handle white */
:deep(.event-disabled-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}

/* Style ToggleSwitch ON state (when voting is disabled) to be orange */
:deep(.event-voting-disabled-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #f97316 !important; /* orange-500 */
}

/* Keep the toggle handle white */
:deep(.event-voting-disabled-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}

/* Style ToggleSwitch ON state (when auto-queue is enabled) to be green */
:deep(.event-auto-queue-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #22c55e !important; /* green-500 */
}

/* Keep the toggle handle white */
:deep(.event-auto-queue-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}

/* Style ToggleSwitch ON state (when setlist is enabled) to be blue */
:deep(.event-setlist-toggle.p-toggleswitch-checked .p-toggleswitch-slider) {
  background-color: #22c55e !important; /* blue-500 */
}

/* Keep the toggle handle white */
:deep(.event-setlist-toggle .p-toggleswitch-slider:before) {
  background-color: white;
}
</style>
